import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Music, Mic, Send, CheckCircle, Instagram, Youtube, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = "";

const VocalistApplication = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    artist_name: '',
    real_name: '',
    email: '',
    phone: '',
    instagram: '',
    youtube: '',
    music_link: '',
    genre: '',
    experience: '',
    song_choice: '',
    why_participate: '',
    additional_info: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.artist_name || !formData.email || !formData.song_choice || !formData.why_participate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/vocalist-applications`, {
        ...formData,
        event_id: 'canonical-event-mac-miller-thundercat-tribute',
        event_name: 'Mac Miller & Thundercat Tribute'
      });
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Failed to submit application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const eventImage = "https://customer-assets.emergentagent.com/job_twincities-music/artifacts/li269nkd_Picsart_26-02-18_07-13-32-079.png";

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-orange-950/20">
        <div className="container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto text-center py-20"
          >
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">Application Submitted!</h1>
            <p className="text-xl text-muted-foreground mb-8">
              Thank you for applying to perform at the Mac Miller & Thundercat Tribute show!
              We'll review your application and get back to you soon.
            </p>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Follow <span className="text-primary">@soul.prime.mn</span> on Instagram for updates!
              </p>
              <Button onClick={() => navigate('/')} className="gap-2">
                <ArrowLeft size={16} />
                Back to Home
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-orange-950/20">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading font-bold">Vocalist Application</h1>
            <p className="text-xs text-muted-foreground">Mac Miller & Thundercat Tribute</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Event Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="relative rounded-xl overflow-hidden">
              <img 
                src={eventImage} 
                alt="Mac Miller & Thundercat Tribute"
                className="w-full h-auto max-h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="text-orange-400 text-sm font-medium uppercase tracking-wider">
                  Mystery Machine Presents
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mt-2">
                  Mac Miller & Thundercat Tribute
                </h2>
                <p className="text-lg text-gray-300 mt-2">
                  March 21st, 2026 • Doors @ 7pm • Featuring Soul Prime
                </p>
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 space-y-6"
          >
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 rounded-full mb-4">
                <Mic className="w-5 h-5 text-orange-400" />
                <span className="text-orange-400 font-semibold">SEEKING NEW LOCAL VOICES!</span>
              </div>
            </div>
            
            <Card className="bg-gradient-to-br from-orange-950/30 to-red-950/30 border-orange-500/30">
              <CardContent className="pt-6 space-y-4">
                <p className="text-lg">
                  Are you a vocalist in the Twin Cities that's stuck looking for beats or a band? 
                  Trying to elevate your skill and reach? 🎙️
                </p>
                <p>
                  We are starting <strong className="text-orange-400">themed showcase workshops</strong> bringing 
                  Twin Cities musicians together to connect and bring each other up! 💪
                </p>
                <p>
                  Connect with real producers and musicians industry professionals to elevate your craft!
                </p>
                <div className="bg-black/30 rounded-lg p-4 border border-orange-500/20">
                  <p className="font-semibold text-orange-400 mb-2">🏆 What You Can Win:</p>
                  <ul className="space-y-2 text-sm">
                    <li>• Live event professionally recorded at an upcoming Minneapolis jazz club</li>
                    <li>• Best performance earns a <strong>professional recording session</strong> with the band</li>
                    <li>• Small VIP audience experience</li>
                    <li>• Video with <strong>all-star placement in the Twin Tube app!</strong></li>
                  </ul>
                </div>
                <div className="pt-2">
                  <p className="font-semibold mb-2">Also featuring songs by:</p>
                  <div className="flex flex-wrap gap-2">
                    {['Mac Miller', 'Thundercat', 'J Cole', 'Anderson .Paak', 'Atmosphere', 'Kendrick Lamar'].map(artist => (
                      <span key={artist} className="px-3 py-1 bg-orange-500/20 rounded-full text-sm">
                        {artist}
                      </span>
                    ))}
                    <span className="px-3 py-1 bg-orange-500/20 rounded-full text-sm">+ More</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Application Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-orange-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Music className="w-5 h-5 text-orange-400" />
                  Vocalist Application Form
                </CardTitle>
                <CardDescription>
                  Fill out the form below to apply for the Local Vocalist Showcase
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-orange-400 border-b border-orange-500/30 pb-2">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="artist_name">Artist/Stage Name *</Label>
                        <Input
                          id="artist_name"
                          name="artist_name"
                          placeholder="Your stage name"
                          value={formData.artist_name}
                          onChange={handleInputChange}
                          required
                          data-testid="artist-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="real_name">Real Name</Label>
                        <Input
                          id="real_name"
                          name="real_name"
                          placeholder="Your real name (optional)"
                          value={formData.real_name}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          data-testid="email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="genre">Musical Style/Genre</Label>
                      <Input
                        id="genre"
                        name="genre"
                        placeholder="e.g., R&B, Hip-Hop, Soul, Neo-Soul"
                        value={formData.genre}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Social & Music Links */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-orange-400 border-b border-orange-500/30 pb-2">
                      Social Media & Music Links
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="instagram" className="flex items-center gap-2">
                          <Instagram size={16} /> Instagram Handle
                        </Label>
                        <Input
                          id="instagram"
                          name="instagram"
                          placeholder="@yourhandle"
                          value={formData.instagram}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="youtube" className="flex items-center gap-2">
                          <Youtube size={16} /> YouTube Channel
                        </Label>
                        <Input
                          id="youtube"
                          name="youtube"
                          placeholder="YouTube channel URL"
                          value={formData.youtube}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="music_link" className="flex items-center gap-2">
                        <LinkIcon size={16} /> Link to Your Music
                      </Label>
                      <Input
                        id="music_link"
                        name="music_link"
                        placeholder="SoundCloud, Spotify, or any link to hear your music"
                        value={formData.music_link}
                        onChange={handleInputChange}
                      />
                      <p className="text-xs text-muted-foreground">
                        Share a link where we can hear your vocals (SoundCloud, Spotify, YouTube, etc.)
                      </p>
                    </div>
                  </div>

                  {/* Performance Details */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-orange-400 border-b border-orange-500/30 pb-2">
                      Performance Details
                    </h3>
                    <div className="space-y-2">
                      <Label htmlFor="experience">Performance Experience</Label>
                      <Textarea
                        id="experience"
                        name="experience"
                        placeholder="Tell us about your performance experience (shows, open mics, etc.)"
                        value={formData.experience}
                        onChange={handleInputChange}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="song_choice">
                        What Mac Miller or Thundercat song would you perform? *
                      </Label>
                      <Textarea
                        id="song_choice"
                        name="song_choice"
                        placeholder="Which song(s) would you want to perform? Feel free to list a few options."
                        value={formData.song_choice}
                        onChange={handleInputChange}
                        rows={3}
                        required
                        data-testid="song-choice-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="why_participate">
                        Why do you want to be part of this show? *
                      </Label>
                      <Textarea
                        id="why_participate"
                        name="why_participate"
                        placeholder="Tell us what this tribute means to you and why you'd be a great addition to the showcase."
                        value={formData.why_participate}
                        onChange={handleInputChange}
                        rows={4}
                        required
                        data-testid="why-participate-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="additional_info">Anything Else?</Label>
                      <Textarea
                        id="additional_info"
                        name="additional_info"
                        placeholder="Any additional information you'd like to share?"
                        value={formData.additional_info}
                        onChange={handleInputChange}
                        rows={2}
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                    disabled={submitting}
                    data-testid="submit-application-btn"
                  >
                    {submitting ? (
                      <>Submitting...</>
                    ) : (
                      <>
                        <Send size={16} />
                        Submit Application
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Footer Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 text-center text-sm text-muted-foreground"
          >
            <p>Questions? DM <span className="text-orange-400">@soul.prime.mn</span> on Instagram</p>
            <p className="mt-1">or contact Mystery Machine Productions</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VocalistApplication;
